import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import roundValueToGivenNumDecimalPlaces from '../../../../utils/roundValueToGivenNumDecimalPlaces';
import { apiClient } from '../../../../api/apiClient';

// ACTIONS
import { getAllocatedProjects, addProjectAllocation } from '../../../../actions/expense';

// SELECTORS
import { getAllocatedProjects as getProjects, getTotalAllocated, getProjectsToAllocate } from '../../../../selectors/expense';
import { getSelectedEntityProjects } from '../../../../selectors/common'
import { getSelectedContainer } from '../../../../selectors/containers'
import { getSelectedEntityObject } from '../../../../selectors/entities'
import AllocatedProject from '../AllocatedProject';


// COMPONENTS
import { Typography } from '@material-ui/core';
import { AddCircle } from '@material-ui/icons';
import Figure from '../Figure';
import Selector from '../../../../commonComponents/FormBuilder/fieldTypes/Selector';
import FormControl from '@material-ui/core/FormControl';

// CONSTANTS
import {
  MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION,
  NO_PROJECTS_TO_ALLOCATE,
  NO_REMAINING_TO_ALLOCATE,
  US_WAGE_EXPENSE_TYPE_REF_ID
} from '../constants';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(2),
  },
  title: {
    margin: theme.spacing(1)
  },
  containerFigures: {
    display: 'flex',
    justifyContent: 'space-around'

  },
  figureContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'

  },
  figure: {
    width: '40px',
    height: '40px',
    background: 'lightGrey',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'

  },
  containerProjects: {
    padding: theme.spacing(1)
  },
  addItem: {
    color: theme.palette.grey[900],
    border: '1px dashed',
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[100],
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
  },
  addItemEnabled: {
    cursor: 'pointer',
    transition: '1s',
    '&:hover': {
      background: theme.palette.grey[400],
    },
  },
  addItemMessages: {
    display: 'flex',
    flexDirection: 'column'
  },
  employeeRole: {
    marginTop: theme.spacing(.5),
    padding: theme.spacing(.5),
    background: theme.palette.grey[100],
    border: theme.palette.grey[200],
    display: 'flex',
    justifyContent: 'space-between'
  }
}))

const ProjectsAllocation = ({ expenseId, qualifiedPercent, isEmployee, employeeId, expenseTypeRefId, employeeProjectRoleMap, setEmployeeProjectRoleMap, createEmployeeProjectRole }) => {
  const style = useStyles();
  const dispatch = useDispatch();
  const projectsToAllocate = useSelector(getProjectsToAllocate, shallowEqual)
  const entityProjects = useSelector(getSelectedEntityProjects, shallowEqual)
  const allocatedProjects = useSelector(getProjects, shallowEqual)
  const totalAllocated = useSelector(getTotalAllocated)
  const selectedContainer = useSelector(getSelectedContainer)
  const selectedEntity = useSelector(getSelectedEntityObject)
  const selectedJurisdictionId = selectedEntity && selectedEntity.jurisdiction && selectedEntity.jurisdiction.jurisdiction_id;
  const containerTaxYear = selectedContainer && selectedContainer.tax_year;
  const [employeeRoles, setEmployeeRoles] = useState([]);

  useEffect(() => {
    if (expenseId) {
      dispatch(getAllocatedProjects(expenseId))
    }
  }, [dispatch, expenseId])

  useEffect(() => {
    if (isEmployee && employeeId && allocatedProjects && allocatedProjects.length && expenseTypeRefId === US_WAGE_EXPENSE_TYPE_REF_ID) {
      const newMap = {};
      let firstItem = null;
      allocatedProjects.forEach(allocatedProject => {
        if (allocatedProject.projectId) {
          const employeeProjectId = `${employeeId}-${allocatedProject.projectId}`;
          newMap[employeeProjectId] = null;
          if (!firstItem) {
            firstItem = employeeProjectId;
          }
        }
      });

      async function fetchEmployeeProjectRoles(currentMap) {
        const responses = await Promise.all(Object.keys(currentMap).map(employeeProjectId => {
          const employeeProjectIdArray = employeeProjectId.split('-');
          return getEmployeeProjectRole(employeeProjectIdArray[0], employeeProjectIdArray[1]);
        }));
        responses.forEach(response => currentMap[`${response.employeeId}-${response.projectId}`] = {
          employeeRoleId: response.employeeRoleId,
          updated: false,
        });
        setEmployeeProjectRoleMap(currentMap);
      };
      fetchEmployeeProjectRoles(newMap);

    }
  }, [allocatedProjects, employeeId, isEmployee, expenseTypeRefId, setEmployeeProjectRoleMap]);


  useEffect(() => {
    async function fetchEmployeeRoles(jurisdictionId, taxYear) {
      const employeeRolesResponse = await getEmployeeRoles(jurisdictionId, taxYear);
      setEmployeeRoles(employeeRolesResponse || []);
    };

    if (isEmployee && employeeId && expenseTypeRefId === US_WAGE_EXPENSE_TYPE_REF_ID && selectedJurisdictionId === 235 && containerTaxYear >= 2024) {
      void fetchEmployeeRoles(selectedJurisdictionId, containerTaxYear)
    }

  }, [selectedJurisdictionId, containerTaxYear, isEmployee, employeeId, expenseTypeRefId]);

  async function getEmployeeProjectRole(employeeId, projectId) {
    const employeeRoles = await apiClient({
      method: 'GET',
      url: `/employee/employee-project-role?employeeId=${employeeId}&projectId=${projectId}`,
    });

    return employeeRoles;
  }

  async function getEmployeeRoles(jurisdictionId, taxYear) {
    const employeeRoles = await apiClient({
      method: 'GET',
      url: `/employee/role?jurisdictionId=${jurisdictionId}&taxYear=${taxYear}`,
    });

    return employeeRoles;
  }

  const handleAdd = () => {
    dispatch(addProjectAllocation(expenseId));
  }

  const totalRemaining = () => {
    let remaining = 0;
    if (totalAllocated && roundValueToGivenNumDecimalPlaces(totalAllocated, 2) < MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION) {
      remaining = MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION - roundValueToGivenNumDecimalPlaces(totalAllocated, 2);
    }
    return remaining;
  }

  const resetEmployeeProjectRole = (projectId, employeeId) => {
    return () => {
      createEmployeeProjectRole({
        employeeId: employeeId,
        projectId: projectId,
        employeeRoleId: 0
      });
    };

  }

  return (
    <div className={style.container}>
      <Typography variant="h6" align={'center'} className={style.title}>Allocate expense to project</Typography>
      <div className={style.containerFigures}>
        <Figure value={qualifiedPercent} title={'Total qualifying'} valueType={'qualifying'} />
        <Figure value={roundValueToGivenNumDecimalPlaces(totalAllocated, 2)} title={'Allocated'} valueType={'allocated'} />
        <Figure value={roundValueToGivenNumDecimalPlaces(totalRemaining(), 2)} title={'Remaining'} valueType={'remaining'} />
      </div>
      <div className={style.containerProjects}>
        {allocatedProjects && allocatedProjects.map((project, index) =>
          <div key={project.projectId}>
            <AllocatedProject key={`allocate-${index}`}
              projects={projectsToAllocate}
              project={project}
              index={index}
              entityProjects={entityProjects}
              remainingToAllocate={MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION - totalAllocated}
              resetEmployeeProjectRole={resetEmployeeProjectRole(project.projectId, employeeId)}
            />
            {(expenseTypeRefId === US_WAGE_EXPENSE_TYPE_REF_ID && selectedJurisdictionId === 235 && containerTaxYear >= 2024 && project && project.projectId) ?
              <FormControl className={style.employeeRole} fullWidth>
                <Selector
                  name="role"
                  label="Role"
                  value={employeeProjectRoleMap[`${employeeId}-${project.projectId}`]?.employeeRoleId || 0}
                  options={employeeRoles}
                  optionKeys={{
                    name: 'name',
                    value: 'id'
                  }}
                  onChange={(e) => {
                    const newRoleId = e.target.value;
                    setEmployeeProjectRoleMap(prev => ({
                      ...prev,
                      [`${employeeId}-${project.projectId}`]: {
                        employeeRoleId: newRoleId,
                        updated: true,
                      }
                    }));
                  }}
                  allowEmptyOption={true}
                  muiProps={{ fullWidth: true }}
                />
              </FormControl> : null
            }
          </div>)}
        <div
          className={`${style.addItem} ${projectsToAllocate.length && totalAllocated < MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION ? style.addItemEnabled : null}`}
          role={'button'}
          tabIndex={projectsToAllocate.length && totalAllocated < MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION ? 0 : null}
          onClick={projectsToAllocate.length && totalAllocated < MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION ? handleAdd : null}
        >
          {Boolean(projectsToAllocate.length) && totalAllocated < MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION && <AddCircle fontSize={'small'} />}
          <div className={style.addItemMessages}>
            {!Boolean(projectsToAllocate.length) && <Typography variant={'caption'}>{NO_PROJECTS_TO_ALLOCATE}</Typography>}
            {totalAllocated === MAXIMUM_PERCENTAGE_FOR_PROJECT_ALLOCATION && <Typography variant={'caption'}>{NO_REMAINING_TO_ALLOCATE}</Typography>}
          </div>
        </div>
      </div>
    </div>
  );
};

ProjectsAllocation.propTypes = {
  expenseId: PropTypes.number,
  qualifiedPercent: PropTypes.number,
  isEmployee: PropTypes.bool,
  employeeId: PropTypes.number,
  expenseTypeRefId: PropTypes.number,
}

export default ProjectsAllocation;
